import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { useSearchParams } from 'react-router';
import { Icon, type IconName } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Confirm, Dialog, EmptyState, Field, Skeleton, StatusBadge, Switch } from '../components/ui.tsx';
import { t, tn } from '../i18n/index.ts';
import { action, ApiError, upload } from '../lib/api.ts';
import { dateTime, day, money, parseMoney } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { useUi } from '../lib/ui-state.tsx';
import { useAction, useSave } from './manage/common.tsx';
import { PayRunsTab } from './PayRuns.tsx';
import { Kpi, Table } from './Reports.tsx';

/* ---------------------------------------------------------------- types */

interface AccountRow {
  id: string;
  name: string;
  iban: string | null;
  bank_name: string | null;
  bic: string | null;
  routing_no: string | null;
  ach_company_id: string | null;
  /** The payment file this account makes: SEPA (IBAN in euros) or ACH (US routing number). */
  pay_file: 'sepa' | 'ach' | null;
  currency: string;
  ledger: string;
  ledger_name: string | null;
  active: boolean;
  books: number;
  statement_balance: number | null;
  statement_date: string | null;
  books_at_statement: number | null;
  open: number;
  open_sum: number;
  last_line: string | null;
}
interface StatementRow {
  id: string;
  number: number;
  account: string;
  format: Format;
  file_name: string;
  from_date: string | null;
  to_date: string | null;
  lines: number;
  added: number;
  open: number;
  at: string;
  by_name: string;
}
interface ChartAccount {
  id: string;
  code: string;
  name: string;
  type?: string;
  key: string | null;
}
interface Rule {
  id: string;
  name: string;
  contains: string;
  direction: Direction;
  account: string;
  account_name: string;
  label: string | null;
  auto: boolean;
  active: boolean;
  used: number;
}
interface Overview {
  base_currency: string;
  accounts: AccountRow[];
  statements: StatementRow[];
  ledgers: ChartAccount[];
  chart: ChartAccount[];
  rules: Rule[];
  can: { reconcile: boolean; manage: boolean; invoices: boolean; bills: boolean; post: boolean; pay: boolean };
}
interface Match {
  id: string;
  kind: 'invoice' | 'bill' | 'posting' | 'existing';
  amount: number;
  label: string | null;
}
interface Line {
  id: string;
  account: string;
  account_name: string;
  statement: string;
  date: string;
  amount: number;
  description: string | null;
  counterparty: string | null;
  counterparty_iban: string | null;
  reference: string | null;
  state: 'open' | 'matched' | 'ignored';
  note: string | null;
  done_at: string | null;
  done_by_name: string | null;
  auto: boolean;
  matches: Match[];
}
interface Lines {
  lines: Line[];
  more: boolean;
  total: number;
  sum: number;
}
type Why = 'amount' | 'number' | 'account' | 'name' | 'date';
interface DocCandidate {
  kind: 'invoice' | 'bill';
  id: string;
  name: string;
  partner: string;
  supplier_ref?: string | null;
  date: string;
  due_date: string;
  open: number;
  score: number;
  why: Why[];
}
interface BookCandidate {
  kind: 'existing';
  id: string;
  number: number;
  date: string;
  amount: number;
  memo: string;
  reference: string | null;
  partner: string | null;
  score: number;
  why: Why[];
}
interface RuleCandidate {
  kind: 'rule';
  id: string;
  name: string;
  account: string;
  account_name: string;
  label: string | null;
}
interface Suggestions {
  line: Line;
  partner: { kind: 'customer' | 'supplier'; name: string } | null;
  invoices: DocCandidate[];
  bills: DocCandidate[];
  existing: BookCandidate[];
  rules: RuleCandidate[];
}
type Format = 'camt' | 'mt940' | 'ofx' | 'sheet';
type Direction = 'any' | 'in' | 'out';
interface Mapping {
  sheet?: string;
  header_row: number;
  date: number;
  value_date?: number | null;
  amount?: number | null;
  credit?: number | null;
  debit?: number | null;
  description: number[];
  counterparty?: number | null;
  counterparty_iban?: number | null;
  reference?: number | null;
  balance?: number | null;
  comma_decimal: boolean;
  month_first: boolean;
  flip?: boolean;
}
interface PreviewLine {
  date: string;
  amount: number;
  description: string;
  counterparty: string | null;
  reference: string | null;
}
interface PreviewStatement {
  account: string | null;
  currency: string | null;
  from: string | null;
  to: string | null;
  opening: number | null;
  closing: number | null;
  count: number;
  money_in: number;
  money_out: number;
  adds_up: boolean | null;
  lines: PreviewLine[];
  suggested_account: string | null;
}
interface ReadResult {
  format: Format;
  statements: PreviewStatement[];
  skipped: { row: number; reason: 'date' | 'amount' }[];
  skipped_count: number;
  sheet: { names: string[]; name: string; header_row: number; columns: { index: number; title: string; samples: string[] }[]; mapping: Mapping } | null;
  already: { number: number; at: string } | null;
}
interface ImportResult {
  statement: { number: number };
  added: number;
  duplicates: number;
  auto_matched: number;
  open: number;
  gap: { expected: number; opening: number } | null;
}

type Tab = 'open' | 'matched' | 'ignored' | 'statements' | 'payments' | 'rules';
const TABS: { key: Tab; icon: IconName }[] = [
  { key: 'open', icon: 'inbox' },
  { key: 'matched', icon: 'checkCircle' },
  { key: 'ignored', icon: 'archive' },
  { key: 'statements', icon: 'file' },
  { key: 'payments', icon: 'send' },
  { key: 'rules', icon: 'sliders' },
];
const FORMAT_LABEL: Record<Format, string> = { camt: 'camt.053', mt940: 'MT940', ofx: 'OFX', sheet: 'CSV / Excel' };
const WHY_ICON: Record<Why, IconName> = { amount: 'cash', number: 'hash', account: 'bank', name: 'contact', date: 'calendar' };
const MAX_BYTES = 8_500_000;
const ACCEPT = '.xml,.sta,.mt940,.940,.txt,.ofx,.qfx,.csv,.tsv,.xlsx,.xlsm';

/** The file as base64, which is how an upload travels in the request. */
function readFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).replace(/^data:[^,]*,/, ''));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

const signed = (n: number) => money(n, { sign: true });
const maskIban = (s: string | null) => (s ? `${s.slice(0, 4)} ···· ${s.slice(-4)}` : '');
const lineTitle = (l: Pick<Line, 'counterparty' | 'description' | 'reference'>) => l.counterparty || l.description || l.reference || '—';

/* ---------------------------------------------------------------- page */

/**
 * Bank: statements in, matched to invoices, bills and the books. Bank accounts at the top with
 * the bank's balance next to the books'; under them the lines still to match, oldest first. A
 * line opens beside its likely matches; matching one moves on to the next.
 */
export function Bank() {
  const { can } = useSession();
  const [params, setParams] = useSearchParams();
  const tab = (TABS.some((x) => x.key === params.get('tab')) ? params.get('tab') : 'open') as Tab;
  const accountId = params.get('account');
  const [version, setVersion] = useState(0);
  const { data, reload } = useAction<Overview>('bank.overview', { v: version });
  const [importing, setImporting] = useState(false);
  const [managing, setManaging] = useState<'list' | 'new' | null>(null);
  const changed = () => {
    setVersion((v) => v + 1);
    void reload();
  };
  const set = (next: Record<string, string | null>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) {
      if (v === null) p.delete(k);
      else p.set(k, v);
    }
    setParams(p, { replace: true });
  };
  const accounts = data?.accounts.filter((a) => a.active) ?? [];
  const account = accounts.find((a) => a.id === accountId) ?? null;
  const open = (account ? [account] : accounts).reduce((n, a) => n + a.open, 0);

  return (
    <Shell>
      <div className="page reports bank">
        <div className="page-head">
          <div>
            <h1>{t('bank.title')}</h1>
            <p className="muted">{t('bank.subtitle')}</p>
          </div>
          <div className="page-actions">
            {data?.can.manage ? (
              <Button variant="secondary" icon="settings" onClick={() => setManaging('list')}>
                {t('bank.accounts')}
              </Button>
            ) : null}
            {data?.can.reconcile && accounts.length ? (
              <Button variant="primary" icon="upload" onClick={() => setImporting(true)}>
                {t('bank.import')}
              </Button>
            ) : null}
          </div>
        </div>

        {!data ? (
          <div className="rep-grid">
            <Skeleton h={120} r="var(--radius-lg)" />
            <Skeleton h={320} r="var(--radius-lg)" />
          </div>
        ) : !accounts.length ? (
          <section className="panel">
            <EmptyState
              icon="bank"
              title={t('bank.none')}
              body={t('bank.none_body')}
              action={
                data.can.manage ? (
                  <Button variant="primary" icon="plus" onClick={() => setManaging('new')}>
                    {t('bank.add_account')}
                  </Button>
                ) : undefined
              }
            />
          </section>
        ) : (
          <>
            <div className="bank-cards" role="radiogroup" aria-label={t('bank.accounts')}>
              {accounts.length > 1 ? (
                <button type="button" role="radio" aria-checked={!account} className={`bank-card bank-card-all${!account ? ' selected' : ''}`} onClick={() => set({ account: null })}>
                  <span className="bank-card-name">{t('bank.all_accounts')}</span>
                  <span className="bank-card-balance tabular">{money(accounts.reduce((s, a) => s + a.books, 0))}</span>
                  <span className="muted small">{t('bank.in_books')}</span>
                  <OpenBadge n={accounts.reduce((s, a) => s + a.open, 0)} />
                </button>
              ) : null}
              {accounts.map((a) => (
                <AccountCard key={a.id} a={a} selected={account?.id === a.id || accounts.length === 1} onClick={() => set({ account: account?.id === a.id ? null : a.id })} />
              ))}
            </div>

            <div className="segmented tabs" role="tablist" aria-label={t('bank.title')}>
              {TABS.filter((x) => (x.key !== 'rules' || data.can.manage || data.rules.length) && (x.key !== 'payments' || data.can.pay)).map((x) => (
                <button key={x.key} role="tab" aria-selected={tab === x.key} className={tab === x.key ? 'active' : ''} onClick={() => set({ tab: x.key === 'open' ? null : x.key })}>
                  <Icon name={x.icon} size={16} />
                  {t(`bank.tab_${x.key}`)}
                  {x.key === 'open' && open ? <span className="count-pill count-pill-warn">{open}</span> : null}
                </button>
              ))}
            </div>

            {tab === 'statements' ? (
              <StatementsTab data={data} account={account} onChanged={changed} />
            ) : tab === 'payments' && data.can.pay ? (
              <PayRunsTab accounts={accounts} version={version} onChanged={changed} />
            ) : tab === 'rules' ? (
              <RulesTab data={data} onChanged={changed} />
            ) : (
              <LinesTab key={`${tab}-${account?.id ?? 'all'}`} state={tab as 'open' | 'matched' | 'ignored'} data={data} account={account} version={version} onChanged={changed} onImport={() => setImporting(true)} />
            )}
          </>
        )}
      </div>
      {importing && data ? (
        <ImportDialog
          data={data}
          initial={account?.id ?? (accounts.length === 1 ? accounts[0]!.id : null)}
          onClose={() => setImporting(false)}
          onDone={(acc) => {
            setImporting(false);
            set({ tab: null, account: accounts.length > 1 ? acc : null });
            changed();
          }}
        />
      ) : null}
      {managing && data ? <AccountsDialog data={data} startNew={managing === 'new'} onClose={() => setManaging(null)} onChanged={changed} /> : null}
    </Shell>
  );
}

function OpenBadge({ n }: { n: number }) {
  return n ? <StatusBadge tone="warn" icon="inbox" label={tn('bank.to_match', n)} /> : <StatusBadge tone="ok" icon="checkCircle" label={t('bank.all_matched')} />;
}

function AccountCard({ a, selected, onClick }: { a: AccountRow; selected: boolean; onClick: () => void }) {
  const diff = a.statement_balance !== null && a.books_at_statement !== null ? a.statement_balance - a.books_at_statement : null;
  return (
    <button type="button" role="radio" aria-checked={selected} className={`bank-card${selected ? ' selected' : ''}`} onClick={onClick}>
      <span className="bank-card-head">
        <span className="bank-card-icon" aria-hidden="true">
          <Icon name="bank" size={18} />
        </span>
        <span className="bank-card-name">{a.name}</span>
      </span>
      <span className="muted small tabular">{[a.bank_name, maskIban(a.iban)].filter(Boolean).join(' · ') || a.ledger_name}</span>
      <span className="bank-card-figures">
        <span>
          <span className="muted small">{t('bank.in_books')}</span>
          <span className="bank-card-balance tabular">{money(a.books)}</span>
        </span>
        <span>
          <span className="muted small">{a.statement_date ? t('bank.bank_on', { date: day(a.statement_date) }) : t('bank.bank_says')}</span>
          <span className="bank-card-balance tabular">{a.statement_balance === null ? '—' : money(a.statement_balance)}</span>
        </span>
      </span>
      <span className="bank-card-foot">
        <OpenBadge n={a.open} />
        {diff === null ? null : diff === 0 ? (
          <span className="muted small">
            <Icon name="checkCircle" size={14} /> {t('bank.agrees')}
          </span>
        ) : (
          <span className="text-warn small" title={t('bank.differs_hint')}>
            <Icon name="alert" size={14} /> {t('bank.differs', { amount: signed(diff) })}
          </span>
        )}
      </span>
    </button>
  );
}

/* ---------------------------------------------------------------- lines */

function LinesTab({
  state,
  data,
  account,
  version,
  onChanged,
  onImport,
}: {
  state: 'open' | 'matched' | 'ignored';
  data: Overview;
  account: AccountRow | null;
  version: number;
  onChanged: () => void;
  onImport: () => void;
}) {
  const { toast, fail } = useUi();
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [direction, setDirection] = useState<Direction>('any');
  const [limit, setLimit] = useState(100);
  const [mine, setMine] = useState(0);
  const { data: list } = useAction<Lines>('bank.lines', { account: account?.id, state, q: query || undefined, direction, limit, v: version + mine });
  const [openLine, setOpenLine] = useState<string | null>(null);
  const [undo, setUndo] = useState<Line | null>(null);
  const [autoBusy, setAutoBusy] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(id);
  }, [q]);
  const lines = list?.lines ?? [];
  const refresh = () => {
    setMine((x) => x + 1);
    onChanged();
  };
  const runAuto = async () => {
    setAutoBusy(true);
    try {
      let n = 0;
      for (const a of account ? [account] : data.accounts.filter((x) => x.active && x.open)) n += (await action<{ matched: number }>('bank.auto_match', { account: a.id })).matched;
      toast(n ? tn('bank.auto_done', n) : t('bank.auto_none'), n ? 'ok' : undefined);
      if (n) refresh();
    } catch (err) {
      fail(err);
    } finally {
      setAutoBusy(false);
    }
  };
  const next = (after: string) => {
    const i = lines.findIndex((l) => l.id === after);
    const rest = [...lines.slice(i + 1), ...lines.slice(0, Math.max(i, 0))].filter((l) => l.id !== after && l.state === 'open');
    return rest[0]?.id ?? null;
  };

  return (
    <div className="rep-grid">
      <div className="toolbar rep-wide">
        <div className="toolbar-group">
          <label className="search">
            <Icon name="search" size={16} />
            <input className="input" type="search" placeholder={t('bank.search')} aria-label={t('bank.search')} value={q} onChange={(e) => setQ(e.target.value)} />
          </label>
          <div className="segmented" role="radiogroup" aria-label={t('bank.direction')}>
            {(['any', 'in', 'out'] as const).map((d) => (
              <button key={d} role="radio" aria-checked={direction === d} className={direction === d ? 'active' : ''} onClick={() => setDirection(d)}>
                {t(`bank.dir_${d}`)}
              </button>
            ))}
          </div>
        </div>
        {state === 'open' && data.can.reconcile && list?.total ? (
          <Button variant="secondary" icon="sparkle" busy={autoBusy} onClick={runAuto}>
            {t('bank.auto')}
          </Button>
        ) : null}
      </div>

      {!list ? (
        <Skeleton h={320} r="var(--radius-lg)" />
      ) : !lines.length ? (
        <section className="panel rep-wide">
          {state === 'open' && !query && direction === 'any' ? (
            <EmptyState
              icon="checkCircle"
              title={t('bank.nothing_open')}
              body={t('bank.nothing_open_body')}
              action={
                data.can.reconcile ? (
                  <Button variant="secondary" icon="upload" onClick={onImport}>
                    {t('bank.import')}
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <EmptyState icon="search" title={t('bank.no_lines')} />
          )}
        </section>
      ) : (
        <section className="panel rep-wide" aria-label={t(`bank.tab_${state}`)}>
          <div className="panel-head">
            <h2>{tn('bank.lines_n', list.total)}</h2>
            <span className={`tabular strong ${list.sum >= 0 ? 'bank-in' : 'bank-out'}`}>{signed(list.sum)}</span>
          </div>
          <ul className="bank-lines">
            {lines.map((l) => (
              <li key={l.id}>
                <button
                  type="button"
                  className="bank-line"
                  onClick={() => (l.state === 'open' && data.can.reconcile ? setOpenLine(l.id) : l.state !== 'open' && data.can.reconcile ? setUndo(l) : undefined)}
                  aria-label={`${day(l.date)} · ${lineTitle(l)} · ${signed(l.amount)}`}
                >
                  <span className="bank-line-date tabular">{day(l.date)}</span>
                  <span className="bank-line-text">
                    <strong>{lineTitle(l)}</strong>
                    <span className="muted small">
                      {[l.counterparty ? l.description : null, l.reference, data.accounts.length > 1 && !account ? l.account_name : null].filter(Boolean).join(' · ') || ' '}
                    </span>
                    {l.state !== 'open' ? (
                      <span className="bank-line-done small">
                        {l.state === 'matched' ? (
                          <>
                            <Icon name={l.auto ? 'sparkle' : 'checkCircle'} size={14} />
                            {l.matches.map((m) => m.label ?? t(`bank.kind_${m.kind}`)).join(', ')}
                          </>
                        ) : (
                          <>
                            <Icon name="archive" size={14} />
                            {l.note}
                          </>
                        )}
                        <span className="muted">
                          {' · '}
                          {l.auto ? t('bank.by_rule') : (l.done_by_name ?? '')} {l.done_at ? dateTime(l.done_at) : ''}
                        </span>
                      </span>
                    ) : null}
                  </span>
                  <span className={`bank-line-amount tabular ${l.amount >= 0 ? 'bank-in' : 'bank-out'}`}>{signed(l.amount)}</span>
                  {l.state === 'open' && data.can.reconcile ? <Icon name="chevronRight" size={18} className="muted" /> : null}
                  {l.state !== 'open' && data.can.reconcile ? <Icon name="undo" size={16} className="muted" /> : null}
                </button>
              </li>
            ))}
          </ul>
          {list.more ? (
            <div className="panel-foot">
              <Button variant="ghost" onClick={() => setLimit((n) => n + 100)}>
                {t('bank.more')}
              </Button>
            </div>
          ) : null}
        </section>
      )}
      {openLine ? (
        <MatchDialog
          key={openLine}
          id={openLine}
          data={data}
          position={{ at: lines.findIndex((l) => l.id === openLine) + 1, of: list?.total ?? 0 }}
          onClose={() => setOpenLine(null)}
          onDone={(goNext) => {
            const after = next(openLine);
            refresh();
            setOpenLine(goNext ? after : null);
          }}
        />
      ) : null}
      {undo ? <UndoDialog line={undo} onClose={() => setUndo(null)} onDone={() => (setUndo(null), refresh())} /> : null}
    </div>
  );
}

function UndoDialog({ line, onClose, onDone }: { line: Line; onClose: () => void; onDone: () => void }) {
  const { busy, run } = useSave();
  const [reason, setReason] = useState('');
  const keeps = line.matches.filter((m) => m.kind === 'invoice' || m.kind === 'bill');
  return (
    <Dialog
      title={line.state === 'matched' ? t('bank.undo_match') : t('bank.undo_ignore')}
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="danger"
            icon="undo"
            busy={busy}
            onClick={() =>
              void run(async () => {
                await action('bank.reopen', { line: line.id, reason: reason.trim() || null });
                onDone();
              }, t('bank.reopened'))
            }
          >
            {t('bank.reopen')}
          </Button>
        </>
      }
    >
      <div className="bank-undo">
        <p>
          <strong>{lineTitle(line)}</strong> · <span className="tabular">{signed(line.amount)}</span> · {day(line.date)}
        </p>
        {line.state === 'matched' ? <p className="muted">{t('bank.undo_body')}</p> : null}
        {keeps.length ? (
          <div className="callout callout-warn" role="note">
            <Icon name="info" size={18} />
            <span>{t('bank.undo_keeps', { docs: keeps.map((m) => m.label).join(', ') })}</span>
          </div>
        ) : null}
        <Field label={t('bank.undo_reason')}>
          <input className="input" value={reason} maxLength={200} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- matching one line */

interface Chosen {
  key: string;
  kind: 'invoice' | 'bill' | 'existing';
  id: string;
  label: string;
  max: number;
  /** Signed, as the bank sees it. */
  amount: number;
}
interface Posting {
  key: string;
  account: string;
  amount: string;
  label: string;
}

function MatchDialog({ id, data, position, onClose, onDone }: { id: string; data: Overview; position: { at: number; of: number }; onClose: () => void; onDone: (next: boolean) => void }) {
  const { fail, toast } = useUi();
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const { data: s } = useAction<Suggestions>('bank.suggest', { line: id, q: query || undefined });
  const [picks, setPicks] = useState<Chosen[]>([]);
  const [postings, setPostings] = useState<Posting[]>([]);
  const [busy, setBusy] = useState(false);
  const [aside, setAside] = useState<string | null>(null);
  const seq = useRef(0);
  useEffect(() => {
    const h = setTimeout(() => setQuery(q.trim()), 300);
    return () => clearTimeout(h);
  }, [q]);
  const line = s?.line ?? null;
  const ledgers = new Set(data.accounts.map((a) => a.ledger));
  const chart = data.chart.filter((c) => !ledgers.has(c.id));
  const postingSum = postings.reduce((n, p) => {
    const v = parseMoney(p.amount);
    return n + (v === null ? 0 : line && line.amount < 0 ? -v : v);
  }, 0);
  const chosen = picks.reduce((n, p) => n + p.amount, 0) + postingSum;
  const left = line ? line.amount - chosen : 0;
  const ready = !!line && (picks.length > 0 || postings.length > 0) && left === 0 && postings.every((p) => p.account && parseMoney(p.amount));

  const toggle = (c: DocCandidate | BookCandidate) => {
    const key = `${c.kind}:${c.id}`;
    if (picks.some((p) => p.key === key)) {
      setPicks((ps) => ps.filter((p) => p.key !== key));
      return;
    }
    if (!line) return;
    const remaining = line.amount - chosen;
    let amount: number;
    let max: number;
    if (c.kind === 'existing') {
      amount = c.amount;
      max = Math.abs(c.amount);
    } else {
      // An invoice brings money in, a bill takes it out; take what is left on the line, up to what is open.
      const sign = c.kind === 'invoice' ? 1 : -1;
      const room = Math.max(0, remaining * sign);
      amount = sign * (room > 0 ? Math.min(room, c.open) : c.open);
      max = c.open;
    }
    const label = c.kind === 'existing' ? t('bank.entry_n', { n: c.number }) : c.kind === 'invoice' ? `INV ${c.name}` : c.supplier_ref ? `${c.name} (${c.supplier_ref})` : c.name;
    setPicks((ps) => [...ps, { key, kind: c.kind, id: c.id, label, max, amount }]);
  };
  const setPickAmount = (key: string, text: string) => {
    const v = parseMoney(text);
    setPicks((ps) => ps.map((p) => (p.key === key ? { ...p, amount: v === null ? 0 : (p.kind === 'bill' ? -1 : 1) * Math.min(v, p.max) } : p)));
  };
  const addPosting = (account = '', label = '') => {
    const rest = Math.abs(left);
    seq.current += 1;
    setPostings((ps) => [...ps, { key: `p${seq.current}`, account, amount: rest ? (rest / 100).toFixed(2) : '', label }]);
  };
  const submit = async (next: boolean) => {
    if (!line || !ready) return;
    setBusy(true);
    try {
      await action('bank.match', {
        line: line.id,
        invoices: picks.filter((p) => p.kind === 'invoice').map((p) => ({ invoice: p.id, amount: p.amount })),
        bills: picks.filter((p) => p.kind === 'bill').map((p) => ({ bill: p.id, amount: -p.amount })),
        existing: picks.filter((p) => p.kind === 'existing').map((p) => p.id),
        postings: postings.map((p) => ({ account: p.account, amount: (line.amount < 0 ? -1 : 1) * (parseMoney(p.amount) ?? 0), label: p.label.trim() || null })),
      });
      toast(t('bank.matched'), 'ok');
      onDone(next);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };
  const ignore = async () => {
    if (!line || !aside) return;
    if (aside.trim().length < 3) {
      fail(new ApiError(400, 'bad_request', t('bank.aside_why')));
      return;
    }
    setBusy(true);
    try {
      await action('bank.ignore', { line: line.id, note: aside.trim() });
      toast(t('bank.set_aside_done'));
      onDone(true);
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  // Nothing open fits: the usual answer is an account (rent, a fee), so its row is ready to fill.
  const firstLoad = useRef(true);
  useEffect(() => {
    if (!s || !firstLoad.current) return;
    firstLoad.current = false;
    if (data.can.post && !s.invoices.length && !s.bills.length && !s.existing.length && !s.rules.length) addPosting();
  }, [s]); // eslint-disable-line react-hooks/exhaustive-deps
  const docs: (DocCandidate | BookCandidate)[] = s ? [...s.invoices, ...s.bills, ...s.existing].sort((a, b) => b.score - a.score) : [];
  const best = docs.filter((c) => c.score >= 50);
  const others = docs.filter((c) => c.score < 50);

  const docRow = (c: DocCandidate | BookCandidate) => {
    const key = `${c.kind}:${c.id}`;
    const pick = picks.find((p) => p.key === key);
    const amount = c.kind === 'existing' ? c.amount : c.kind === 'invoice' ? c.open : -c.open;
    return (
      <li key={key} className={`bank-cand${pick ? ' picked' : ''}`}>
        <label className="bank-cand-main">
          <input type="checkbox" checked={!!pick} onChange={() => toggle(c)} />
          <span className="bank-cand-icon" aria-hidden="true">
            <Icon name={c.kind === 'invoice' ? 'invoice' : c.kind === 'bill' ? 'receipt' : 'book'} size={18} />
          </span>
          <span className="bank-cand-text">
            <strong>{c.kind === 'existing' ? t('bank.entry_n', { n: c.number }) : c.kind === 'invoice' ? `INV ${c.name}` : c.name}</strong>
            <span className="muted small">
              {c.kind === 'existing'
                ? [c.partner, c.memo, day(c.date)].filter(Boolean).join(' · ')
                : [c.partner, c.kind === 'bill' && c.supplier_ref ? c.supplier_ref : null, t('bank.due', { date: day(c.due_date) })].filter(Boolean).join(' · ')}
            </span>
            {c.why.length ? (
              <span className="bank-why">
                {c.why.map((w) => (
                  <span key={w} className="bank-why-chip">
                    <Icon name={WHY_ICON[w]} size={12} />
                    {t(`bank.why_${w}`)}
                  </span>
                ))}
              </span>
            ) : null}
          </span>
          <span className="bank-cand-amount tabular">
            {signed(amount)}
            {c.kind !== 'existing' ? <span className="muted small">{t('bank.open_amount')}</span> : null}
          </span>
        </label>
        {pick && pick.kind !== 'existing' ? (
          <div className="bank-cand-pay">
            <Field label={t('bank.pay_amount')}>
              <input className="input tabular" inputMode="decimal" defaultValue={(Math.abs(pick.amount) / 100).toFixed(2)} onChange={(e) => setPickAmount(key, e.target.value)} />
            </Field>
            {Math.abs(pick.amount) < pick.max ? <span className="muted small">{t('bank.part_paid', { rest: money(pick.max - Math.abs(pick.amount)) })}</span> : null}
          </div>
        ) : null}
      </li>
    );
  };

  return (
    <Dialog
      title={position.of > 1 && position.at > 0 ? t('bank.match_title_n', { at: position.at, of: position.of }) : t('bank.match_title')}
      onClose={onClose}
      size="lg"
      footer={
        line ? (
          <div className="bank-foot">
            <div className={`bank-left ${ready ? 'ok' : ''}`} aria-live="polite">
              {picks.length || postings.length ? (
                left === 0 && !ready ? (
                  <span className="muted">{t('bank.need_account')}</span>
                ) : left === 0 ? (
                  <>
                    <Icon name="checkCircle" size={16} /> {t('bank.balanced')}
                  </>
                ) : (
                  <>
                    <span className="muted">{t('bank.left')}</span> <strong className="tabular">{signed(left)}</strong>
                  </>
                )
              ) : (
                <span className="muted">{t('bank.pick_hint')}</span>
              )}
            </div>
            <Button variant="ghost" onClick={() => setAside(aside === null ? '' : null)}>
              {t('bank.set_aside')}
            </Button>
            <Button variant="secondary" disabled={!ready} busy={busy} onClick={() => void submit(false)}>
              {t('bank.match')}
            </Button>
            <Button variant="primary" icon="check" disabled={!ready} busy={busy} onClick={() => void submit(true)}>
              {t('bank.match_next')}
            </Button>
          </div>
        ) : undefined
      }
    >
      {!s || !line ? (
        <Skeleton h={260} r="var(--radius-lg)" />
      ) : (
        <div className="bank-match">
          <section className="bank-subject" aria-label={t('bank.the_line')}>
            <div>
              <span className="muted small">
                {day(line.date)} · {line.account_name}
              </span>
              <strong className="bank-subject-title">{lineTitle(line)}</strong>
              {line.description && line.counterparty ? <span className="bank-subject-desc">{line.description}</span> : null}
              <span className="muted small tabular">{[line.reference ? `${t('bank.ref')} ${line.reference}` : null, line.counterparty_iban].filter(Boolean).join(' · ')}</span>
              {s.partner ? (
                <span className="bank-known small">
                  <Icon name="badgeCheck" size={14} /> {t(s.partner.kind === 'customer' ? 'bank.known_customer' : 'bank.known_supplier', { name: s.partner.name })}
                </span>
              ) : null}
            </div>
            <span className={`bank-subject-amount tabular ${line.amount >= 0 ? 'bank-in' : 'bank-out'}`}>{signed(line.amount)}</span>
          </section>

          {aside !== null ? (
            <div className="bank-aside">
              <Field label={t('bank.aside_label')} hint={t('bank.aside_hint')}>
                <input className="input" value={aside} maxLength={200} autoFocus onChange={(e) => setAside(e.target.value)} />
              </Field>
              <Button variant="primary" icon="archive" busy={busy} onClick={() => void ignore()}>
                {t('bank.set_aside')}
              </Button>
            </div>
          ) : null}

          <label className="search bank-find">
            <Icon name="search" size={16} />
            <input className="input" type="search" placeholder={t('bank.find')} aria-label={t('bank.find')} value={q} onChange={(e) => setQ(e.target.value)} />
          </label>

          {s.rules.length ? (
            <div className="bank-rules">
              {s.rules.map((r) => (
                <button
                  key={r.id}
                  type="button"
                  className="bank-rule-chip"
                  onClick={() => (
                    setPostings([]),
                    setPicks([]),
                    (seq.current += 1),
                    setPostings([{ key: `p${seq.current}`, account: r.account, amount: (Math.abs(line.amount) / 100).toFixed(2), label: r.label ?? r.name }])
                  )}
                >
                  <Icon name="sliders" size={14} />
                  {t('bank.use_rule', { name: r.name, account: r.account_name })}
                </button>
              ))}
            </div>
          ) : null}

          {docs.length ? (
            <>
              {best.length ? (
                <section aria-labelledby="bank-best">
                  <h3 id="bank-best" className="bank-h">
                    {query ? t('bank.found') : t('bank.likely')}
                  </h3>
                  <ul className="bank-cands">{best.map(docRow)}</ul>
                </section>
              ) : null}
              {others.length ? (
                <section aria-labelledby="bank-others">
                  <h3 id="bank-others" className="bank-h">
                    {best.length ? t('bank.also') : query ? t('bank.found') : t('bank.maybe')}
                  </h3>
                  <ul className="bank-cands">{others.map(docRow)}</ul>
                </section>
              ) : null}
            </>
          ) : (
            <p className="muted bank-none">{query ? t('bank.nothing_found') : line.amount > 0 ? t('bank.no_suggest_in') : t('bank.no_suggest_out')}</p>
          )}

          {data.can.post ? (
            <section aria-labelledby="bank-post">
              <h3 id="bank-post" className="bank-h">
                {t('bank.post_to')}
              </h3>
              {postings.map((p) => (
                <div key={p.key} className="bank-posting">
                  <Field label={t('bank.account_in_books')}>
                    <select className="input" value={p.account} onChange={(e) => setPostings((ps) => ps.map((x) => (x.key === p.key ? { ...x, account: e.target.value } : x)))}>
                      <option value="">{t('bank.choose_account')}</option>
                      {chart.map((c) => (
                        <option key={c.id} value={c.id}>
                          {c.code} {c.name}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label={t('bank.amount')}>
                    <input
                      className="input tabular"
                      inputMode="decimal"
                      value={p.amount}
                      onChange={(e) => setPostings((ps) => ps.map((x) => (x.key === p.key ? { ...x, amount: e.target.value } : x)))}
                    />
                  </Field>
                  <Field label={t('bank.label')}>
                    <input
                      className="input"
                      value={p.label}
                      maxLength={120}
                      placeholder={t('bank.label_ph')}
                      onChange={(e) => setPostings((ps) => ps.map((x) => (x.key === p.key ? { ...x, label: e.target.value } : x)))}
                    />
                  </Field>
                  <Button variant="ghost" icon="trash" aria-label={t('bank.remove')} onClick={() => setPostings((ps) => ps.filter((x) => x.key !== p.key))} />
                </div>
              ))}
              <Button variant="ghost" icon="plus" onClick={() => addPosting()}>
                {postings.length ? t('bank.add_posting_more') : t('bank.add_posting')}
              </Button>
            </section>
          ) : null}
        </div>
      )}
    </Dialog>
  );
}

/* ---------------------------------------------------------------- importing */

const MAP_FIELDS = ['date', 'amount', 'credit', 'debit', 'counterparty', 'reference', 'counterparty_iban', 'balance', 'value_date'] as const;

function ImportDialog({ data, initial, onClose, onDone }: { data: Overview; initial: string | null; onClose: () => void; onDone: (account: string) => void }) {
  const { fail, toast } = useUi();
  const [file, setFile] = useState<{ name: string; size: number; data: string } | null>(null);
  const [read, setRead] = useState<ReadResult | null>(null);
  const [mapping, setMapping] = useState<Mapping | null>(null);
  const [account, setAccount] = useState<string>(initial ?? '');
  const [index, setIndex] = useState(0);
  const [reading, setReading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<ImportResult | null>(null);
  const [drag, setDrag] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const accounts = data.accounts.filter((a) => a.active);
  const twoColumns = mapping ? mapping.amount === null || mapping.amount === undefined : false;

  const load = async (f: File | undefined) => {
    if (!f) return;
    if (f.size > MAX_BYTES) {
      fail(new ApiError(400, 'too_large', t('bank.too_large')));
      return;
    }
    setReading(true);
    try {
      const b64 = await readFile(f);
      const r = await upload<ReadResult>('bank.read', { file: b64, name: f.name, account: account || undefined });
      setFile({ name: f.name, size: f.size, data: b64 });
      setRead(r);
      setMapping(r.sheet?.mapping ?? null);
      setIndex(0);
      const suggested = r.statements[0]?.suggested_account;
      if (suggested) setAccount(suggested);
    } catch (err) {
      fail(err);
    } finally {
      setReading(false);
      if (input.current) input.current.value = '';
    }
  };

  // A spreadsheet is read again whenever a column choice changes, so the preview is what will come in.
  const remap = async (next: Mapping) => {
    setMapping(next);
    if (!file) return;
    try {
      setRead(await upload<ReadResult>('bank.read', { file: file.data, name: file.name, account: account || undefined, mapping: next }));
    } catch (err) {
      fail(err);
    }
  };

  const go = async () => {
    if (!file || !account) return;
    setBusy(true);
    try {
      const r = await upload<ImportResult>('bank.import', { file: file.data, name: file.name, account, index, ...(mapping ? { mapping } : {}) });
      setDone(r);
      toast(tn('bank.imported', r.added), 'ok');
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const st = read?.statements[index] ?? null;
  const acc = accounts.find((a) => a.id === account) ?? null;
  const columnOptions = read?.sheet?.columns ?? [];
  const col = (k: (typeof MAP_FIELDS)[number]) => (mapping?.[k] ?? null) as number | null;
  const setCol = (k: (typeof MAP_FIELDS)[number], v: number | null) => mapping && void remap({ ...mapping, [k]: v });

  if (done) {
    return (
      <Dialog
        title={t('bank.import_done_title', { n: done.statement.number })}
        onClose={() => onDone(account)}
        size="sm"
        footer={
          <Button variant="primary" icon="check" onClick={() => onDone(account)} data-autofocus>
            {done.open ? t('bank.start_matching') : t('common.close')}
          </Button>
        }
      >
        <div className="bank-done">
          <section className="rep-kpis" aria-label={t('bank.import_done_title', { n: done.statement.number })}>
            <Kpi label={t('bank.k_added')} value={String(done.added)} hint={done.duplicates ? tn('bank.k_dups', done.duplicates) : undefined} />
            <Kpi label={t('bank.k_auto')} value={String(done.auto_matched)} />
            <Kpi label={t('bank.k_open')} value={String(done.open)} />
          </section>
          {done.gap ? (
            <div className="callout callout-warn" role="alert">
              <Icon name="alert" size={18} />
              <span>{t('bank.gap', { opening: money(done.gap.opening), expected: money(done.gap.expected) })}</span>
            </div>
          ) : null}
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      title={t('bank.import_title')}
      onClose={onClose}
      size="lg"
      footer={
        file && st ? (
          <>
            <Button variant="ghost" icon="repeat" onClick={() => (setFile(null), setRead(null), setMapping(null))}>
              {t('bank.other_file')}
            </Button>
            <Button variant="primary" icon="upload" busy={busy} disabled={!account || !st.count || !!read?.already} onClick={() => void go()}>
              {tn('bank.import_n', st.count)}
            </Button>
          </>
        ) : undefined
      }
    >
      <div className="bank-import">
        {!file || !read ? (
          <div
            className={`imp-drop${drag ? ' over' : ''}`}
            onDragOver={(e) => {
              e.preventDefault();
              setDrag(true);
            }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e: DragEvent) => {
              e.preventDefault();
              setDrag(false);
              void load(e.dataTransfer.files[0]);
            }}
          >
            <Icon name="upload" size={28} />
            <p>
              <strong>{t('bank.drop')}</strong>
            </p>
            <p className="muted small">{t('bank.drop_hint')}</p>
            <input ref={input} type="file" accept={ACCEPT} hidden onChange={(e) => void load(e.target.files?.[0])} />
            <Button variant="primary" icon="file" busy={reading} onClick={() => input.current?.click()}>
              {t('bank.choose_file')}
            </Button>
          </div>
        ) : (
          <>
            <div className="imp-file">
              <span className="imp-file-icon" aria-hidden="true">
                <Icon name="file" size={22} />
              </span>
              <div className="imp-file-text">
                <strong>{file.name}</strong>
                <span className="muted small">
                  {FORMAT_LABEL[read.format]} · {(file.size / 1024).toFixed(file.size < 102_400 ? 1 : 0)} KB
                </span>
              </div>
            </div>
            {read.already ? (
              <div className="callout callout-warn" role="alert">
                <Icon name="alert" size={18} />
                <span>{t('bank.already', { n: read.already.number, at: dateTime(read.already.at) })}</span>
              </div>
            ) : null}
            {read.statements.length > 1 ? (
              <Field label={t('bank.which_statement')}>
                <select className="input" value={index} onChange={(e) => setIndex(Number(e.target.value))}>
                  {read.statements.map((x, i) => (
                    <option key={i} value={i}>
                      {[x.account, x.from && x.to ? `${day(x.from)} – ${day(x.to)}` : null, tn('bank.lines_n', x.count)].filter(Boolean).join(' · ')}
                    </option>
                  ))}
                </select>
              </Field>
            ) : null}
            <Field label={t('bank.into_account')} hint={st?.account ? t('bank.file_account', { account: st.account }) : undefined} required>
              <select className="input" value={account} onChange={(e) => setAccount(e.target.value)}>
                <option value="">{t('bank.choose_bank_account')}</option>
                {accounts.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                    {a.iban ? ` · ${maskIban(a.iban)}` : ''}
                  </option>
                ))}
              </select>
            </Field>

            {read.sheet && mapping ? (
              <section className="bank-map" aria-labelledby="bank-map-h">
                <h3 id="bank-map-h" className="bank-h">
                  {t('bank.columns')}
                </h3>
                <p className="muted small">{t('bank.columns_lead', { row: read.sheet.header_row + 1 })}</p>
                <div className="bank-map-grid">
                  <Field label={t('bank.col_date')} required>
                    <ColumnSelect value={col('date')} options={columnOptions} required onChange={(v) => setCol('date', v)} />
                  </Field>
                  <Field label={t('bank.amount_layout')}>
                    <select
                      className="input"
                      value={twoColumns ? 'two' : 'one'}
                      onChange={(e) =>
                        void remap(
                          e.target.value === 'two'
                            ? { ...mapping, amount: null, credit: mapping.credit ?? null, debit: mapping.debit ?? null }
                            : { ...mapping, amount: mapping.amount ?? mapping.credit ?? 0, credit: null, debit: null },
                        )
                      }
                    >
                      <option value="one">{t('bank.layout_one')}</option>
                      <option value="two">{t('bank.layout_two')}</option>
                    </select>
                  </Field>
                  {twoColumns ? (
                    <>
                      <Field label={t('bank.col_credit')}>
                        <ColumnSelect value={col('credit')} options={columnOptions} onChange={(v) => setCol('credit', v)} />
                      </Field>
                      <Field label={t('bank.col_debit')}>
                        <ColumnSelect value={col('debit')} options={columnOptions} onChange={(v) => setCol('debit', v)} />
                      </Field>
                    </>
                  ) : (
                    <Field label={t('bank.col_amount')} required>
                      <ColumnSelect value={col('amount')} options={columnOptions} required onChange={(v) => setCol('amount', v)} />
                    </Field>
                  )}
                  <fieldset className="bank-desc-cols">
                    <legend className="field-label">{t('bank.col_description')}</legend>
                    <div className="bank-chips">
                      {columnOptions.map((c) => {
                        const on = mapping.description.includes(c.index);
                        return (
                          <button
                            key={c.index}
                            type="button"
                            className={`bank-col-chip${on ? ' on' : ''}`}
                            aria-pressed={on}
                            onClick={() => void remap({ ...mapping, description: on ? mapping.description.filter((i) => i !== c.index) : [...mapping.description, c.index].sort((a, b) => a - b) })}
                          >
                            {on ? <Icon name="check" size={14} /> : null}
                            {c.title}
                          </button>
                        );
                      })}
                    </div>
                    <span className="field-hint">{t('bank.col_description_hint')}</span>
                  </fieldset>
                  <Field label={t('bank.col_counterparty')}>
                    <ColumnSelect value={col('counterparty')} options={columnOptions} onChange={(v) => setCol('counterparty', v)} />
                  </Field>
                  <Field label={t('bank.col_reference')}>
                    <ColumnSelect value={col('reference')} options={columnOptions} onChange={(v) => setCol('reference', v)} />
                  </Field>
                  <Field label={t('bank.col_iban')}>
                    <ColumnSelect value={col('counterparty_iban')} options={columnOptions} onChange={(v) => setCol('counterparty_iban', v)} />
                  </Field>
                  <Field label={t('bank.col_balance')} hint={t('bank.col_balance_hint')}>
                    <ColumnSelect value={col('balance')} options={columnOptions} onChange={(v) => setCol('balance', v)} />
                  </Field>
                  <Field label={t('bank.decimal')}>
                    <select className="input" value={mapping.comma_decimal ? 'comma' : 'dot'} onChange={(e) => void remap({ ...mapping, comma_decimal: e.target.value === 'comma' })}>
                      <option value="comma">1.234,56</option>
                      <option value="dot">1,234.56</option>
                    </select>
                  </Field>
                  <Field label={t('bank.date_order')}>
                    <select className="input" value={mapping.month_first ? 'mdy' : 'dmy'} onChange={(e) => void remap({ ...mapping, month_first: e.target.value === 'mdy' })}>
                      <option value="dmy">{t('bank.dmy')}</option>
                      <option value="mdy">{t('bank.mdy')}</option>
                    </select>
                  </Field>
                </div>
                {!twoColumns ? <SwitchRow label={t('bank.flip')} hint={t('bank.flip_hint')} checked={!!mapping.flip} onChange={(v) => void remap({ ...mapping, flip: v })} /> : null}
                {read.skipped_count ? (
                  <p className="muted small">
                    <Icon name="info" size={14} /> {tn('bank.skipped', read.skipped_count)}{' '}
                    {read.skipped
                      .slice(0, 5)
                      .map((x) => t(x.reason === 'date' ? 'bank.skip_date' : 'bank.skip_amount', { row: x.row }))
                      .join(' · ')}
                  </p>
                ) : null}
              </section>
            ) : null}

            {st ? (
              <section aria-labelledby="bank-prev-h">
                <h3 id="bank-prev-h" className="bank-h">
                  {st.from && st.to ? t('bank.period', { from: day(st.from), to: day(st.to) }) : t('bank.preview')}
                </h3>
                <section className="rep-kpis bank-prev-kpis" aria-label={t('bank.preview')}>
                  <Kpi label={t('bank.k_lines')} value={String(st.count)} />
                  <Kpi label={t('bank.k_in')} value={money(st.money_in)} />
                  <Kpi label={t('bank.k_out')} value={money(st.money_out)} />
                  {st.closing !== null ? (
                    <Kpi label={t('bank.k_closing')} value={money(st.closing)} hint={st.opening !== null ? t('bank.k_opening', { amount: money(st.opening) }) : undefined} />
                  ) : null}
                </section>
                {st.adds_up === false ? (
                  <div className="callout callout-warn" role="status">
                    <Icon name="alert" size={18} />
                    <span>{t('bank.not_adding')}</span>
                  </div>
                ) : null}
                {acc && st.account && acc.iban && !sameEnd(acc.iban, st.account) ? (
                  <div className="callout callout-warn" role="alert">
                    <Icon name="alert" size={18} />
                    <span>{t('bank.other_account', { file: st.account, account: acc.name })}</span>
                  </div>
                ) : null}
                <Table
                  head={[t('bank.c_date'), t('bank.c_text'), t('bank.c_amount')]}
                  left={[1]}
                  empty={t('bank.no_lines_file')}
                  rows={st.lines.map((l, i) => ({
                    key: String(i),
                    cells: [
                      <span className="tabular">{day(l.date)}</span>,
                      <span>
                        {lineTitle({ counterparty: l.counterparty, description: l.description, reference: l.reference })}
                        {l.counterparty && l.description ? <span className="muted small"> · {l.description}</span> : null}
                      </span>,
                      <span className={`tabular ${l.amount >= 0 ? 'bank-in' : 'bank-out'}`}>{signed(l.amount)}</span>,
                    ],
                  }))}
                />
                {st.count > st.lines.length ? <p className="muted small">{t('bank.and_more', { n: st.count - st.lines.length })}</p> : null}
              </section>
            ) : null}
          </>
        )}
      </div>
    </Dialog>
  );
}

function SwitchRow({ label, hint, checked, onChange }: { label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <div className="setting-row">
      <div>
        <strong>{label}</strong>
        {hint ? <p className="muted">{hint}</p> : null}
      </div>
      <Switch checked={checked} onChange={onChange} label={label} />
    </div>
  );
}

const sameEnd = (a: string, b: string) => {
  const x = a.replace(/\s+/g, '').toUpperCase();
  const y = b.replace(/\s+/g, '').toUpperCase();
  return x === y || (y.length >= 6 && x.endsWith(y)) || (x.length >= 6 && y.endsWith(x));
};

function ColumnSelect({
  value,
  options,
  required,
  onChange,
}: {
  value: number | null;
  options: { index: number; title: string; samples: string[] }[];
  required?: boolean;
  onChange: (v: number | null) => void;
}) {
  return (
    <select className="input" value={value ?? ''} onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))}>
      <option value="">{required ? t('bank.choose_column') : t('bank.no_column')}</option>
      {options.map((c) => (
        <option key={c.index} value={c.index}>
          {c.title}
          {c.samples[0] ? ` — ${c.samples[0]}` : ''}
        </option>
      ))}
    </select>
  );
}

/* ---------------------------------------------------------------- statements */

function StatementsTab({ data, account, onChanged }: { data: Overview; account: AccountRow | null; onChanged: () => void }) {
  const { busy, run } = useSave();
  const [removing, setRemoving] = useState<StatementRow | null>(null);
  const rows = data.statements.filter((s) => !account || s.account === account.id);
  const name = (id: string) => data.accounts.find((a) => a.id === id)?.name ?? '';
  return (
    <div className="rep-grid">
      <section className="panel rep-wide">
        <div className="panel-head">
          <h2>{t('bank.statements')}</h2>
        </div>
        <Table
          view="bank.statements"
          head={[t('bank.c_number'), t('bank.c_account'), t('bank.c_period'), t('bank.c_lines'), t('bank.c_open'), t('bank.c_by'), '']}
          phoneHide={[1, 5]}
          left={[1, 5]}
          empty={t('bank.no_statements')}
          rows={rows.map((s) => ({
            key: s.id,
            cells: [
              <span className="tabular">
                {s.number}
                <span className="muted small"> · {FORMAT_LABEL[s.format]}</span>
              </span>,
              name(s.account),
              <span className="tabular">{s.from_date && s.to_date ? (s.from_date === s.to_date ? day(s.from_date) : `${day(s.from_date)} – ${day(s.to_date)}`) : '—'}</span>,
              <span className="tabular" title={s.lines !== s.added ? tn('bank.k_dups', s.lines - s.added) : undefined}>
                {s.added}
                {s.lines !== s.added ? <span className="muted">/{s.lines}</span> : null}
              </span>,
              s.open ? <StatusBadge tone="warn" icon="inbox" label={String(s.open)} /> : <StatusBadge tone="ok" icon="check" label="0" />,
              <span className="small">
                {s.by_name}
                <span className="muted"> · {dateTime(s.at)}</span>
              </span>,
              data.can.manage && s.open === s.added ? <Button size="sm" variant="ghost" icon="trash" aria-label={t('bank.remove_statement', { n: s.number })} onClick={() => setRemoving(s)} /> : null,
            ],
          }))}
        />
      </section>
      {removing ? (
        <Confirm
          title={t('bank.remove_statement', { n: removing.number })}
          body={t('bank.remove_statement_body', { file: removing.file_name, n: removing.added })}
          confirm={t('bank.remove')}
          danger
          busy={busy}
          onClose={() => setRemoving(null)}
          onConfirm={() =>
            void run(async () => {
              await action('bank.delete_statement', { id: removing.id });
              setRemoving(null);
              onChanged();
            }, t('bank.statement_removed'))
          }
        />
      ) : null}
    </div>
  );
}

/* ---------------------------------------------------------------- rules */

function RulesTab({ data, onChanged }: { data: Overview; onChanged: () => void }) {
  const [editing, setEditing] = useState<Rule | 'new' | null>(null);
  const { busy, run } = useSave();
  const [removing, setRemoving] = useState<Rule | null>(null);
  return (
    <div className="rep-grid">
      <section className="panel rep-wide">
        <div className="panel-head">
          <div>
            <h2>{t('bank.rules')}</h2>
            <p className="muted small">{t('bank.rules_lead')}</p>
          </div>
          {data.can.manage ? (
            <Button variant="primary" icon="plus" onClick={() => setEditing('new')}>
              {t('bank.new_rule')}
            </Button>
          ) : null}
        </div>
        {!data.rules.length ? (
          <EmptyState icon="sliders" title={t('bank.no_rules')} body={t('bank.no_rules_body')} />
        ) : (
          <Table
            head={[t('bank.c_rule'), t('bank.c_words'), t('bank.c_posts_to'), t('bank.c_used'), '']}
            phoneHide={[1, 3]}
            left={[1, 2]}
            rows={data.rules.map((r) => ({
              key: r.id,
              onClick: data.can.manage ? () => setEditing(r) : undefined,
              cells: [
                <span>
                  <strong>{r.name}</strong>
                  <span className="bank-rule-flags">
                    {r.direction !== 'any' ? <span className="muted small">{t(`bank.dir_${r.direction}`)}</span> : null}
                    {!r.active ? (
                      <StatusBadge tone="neutral" icon="pause" label={t('bank.rule_off')} />
                    ) : r.auto ? (
                      <StatusBadge tone="ok" icon="sparkle" label={t('bank.rule_auto')} />
                    ) : (
                      <StatusBadge tone="info" icon="eye" label={t('bank.rule_suggest')} />
                    )}
                  </span>
                </span>,
                <span className="muted">{r.contains}</span>,
                r.account_name,
                <span className="tabular">{r.used}</span>,
                data.can.manage ? (
                  <Button
                    size="sm"
                    variant="ghost"
                    icon="trash"
                    aria-label={t('bank.remove_rule', { name: r.name })}
                    onClick={(e) => {
                      e.stopPropagation();
                      setRemoving(r);
                    }}
                  />
                ) : null,
              ],
            }))}
          />
        )}
      </section>
      {editing ? <RuleDialog data={data} rule={editing === 'new' ? null : editing} onClose={() => setEditing(null)} onDone={() => (setEditing(null), onChanged())} /> : null}
      {removing ? (
        <Confirm
          title={t('bank.remove_rule', { name: removing.name })}
          body={t('bank.remove_rule_body')}
          confirm={t('bank.remove')}
          danger
          busy={busy}
          onClose={() => setRemoving(null)}
          onConfirm={() =>
            void run(async () => {
              await action('bank.delete_rule', { id: removing.id });
              setRemoving(null);
              onChanged();
            }, t('bank.rule_removed'))
          }
        />
      ) : null}
    </div>
  );
}

function RuleDialog({ data, rule, onClose, onDone }: { data: Overview; rule: Rule | null; onClose: () => void; onDone: () => void }) {
  const { busy, run } = useSave();
  const [form, setForm] = useState({
    name: rule?.name ?? '',
    contains: rule?.contains ?? '',
    direction: rule?.direction ?? ('out' as Direction),
    account: rule?.account ?? '',
    label: rule?.label ?? '',
    auto: rule?.auto ?? true,
    active: rule?.active ?? true,
  });
  const ledgers = new Set(data.accounts.map((a) => a.ledger));
  const chart = useMemo(() => data.chart.filter((c) => !ledgers.has(c.id)), [data]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = () =>
    void run(async () => {
      await action('bank.save_rule', { ...(rule ? { id: rule.id } : {}), ...form, label: form.label.trim() || null });
      onDone();
    }, t('bank.rule_saved'));
  return (
    <Dialog
      title={rule ? t('bank.edit_rule') : t('bank.new_rule')}
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" icon="check" busy={busy} disabled={!form.name.trim() || !form.contains.trim() || !form.account} onClick={save}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="form-grid">
        <Field label={t('bank.rule_name')} required>
          <input className="input" value={form.name} maxLength={80} placeholder={t('bank.rule_name_ph')} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </Field>
        <Field label={t('bank.rule_words')} hint={t('bank.rule_words_hint')} required>
          <input className="input" value={form.contains} maxLength={300} placeholder={t('bank.rule_words_ph')} onChange={(e) => setForm({ ...form, contains: e.target.value })} />
        </Field>
        <Field label={t('bank.direction')}>
          <select className="input" value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value as Direction })}>
            {(['out', 'in', 'any'] as const).map((d) => (
              <option key={d} value={d}>
                {t(`bank.dir_${d}`)}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('bank.account_in_books')} required>
          <select className="input" value={form.account} onChange={(e) => setForm({ ...form, account: e.target.value })}>
            <option value="">{t('bank.choose_account')}</option>
            {chart.map((c) => (
              <option key={c.id} value={c.id}>
                {c.code} {c.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={t('bank.label')}>
          <input className="input" value={form.label} maxLength={120} placeholder={t('bank.label_ph')} onChange={(e) => setForm({ ...form, label: e.target.value })} />
        </Field>
        <SwitchRow label={t('bank.rule_auto_label')} hint={t('bank.rule_auto_hint')} checked={form.auto} onChange={(v) => setForm({ ...form, auto: v })} />
        {rule ? <SwitchRow label={t('bank.rule_active')} checked={form.active} onChange={(v) => setForm({ ...form, active: v })} /> : null}
      </div>
    </Dialog>
  );
}

/* ---------------------------------------------------------------- bank accounts */

function AccountsDialog({ data, startNew, onClose, onChanged }: { data: Overview; startNew: boolean; onClose: () => void; onChanged: () => void }) {
  const [editing, setEditing] = useState<AccountRow | 'new' | null>(startNew ? 'new' : null);
  const { busy, run } = useSave();
  const bankKey = data.ledgers.find((l) => l.key === 'bank')?.id ?? '';
  const blank = { name: '', iban: '', bank_name: '', bic: '', routing_no: '', ach_company_id: '', ledger: bankKey, active: true };
  const [form, setForm] = useState(blank);
  const edit = (a: AccountRow | 'new') => {
    setEditing(a);
    setForm(
      a === 'new'
        ? blank
        : { name: a.name, iban: a.iban ?? '', bank_name: a.bank_name ?? '', bic: a.bic ?? '', routing_no: a.routing_no ?? '', ach_company_id: a.ach_company_id ?? '', ledger: a.ledger, active: a.active },
    );
  };
  useEffect(() => {
    if (startNew) edit('new');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const save = () =>
    void run(async () => {
      await action('bank.save_account', {
        ...(editing && editing !== 'new' ? { id: editing.id } : {}),
        name: form.name,
        iban: form.iban || null,
        bank_name: form.bank_name || null,
        bic: us ? null : form.bic.trim() || null,
        routing_no: us ? form.routing_no.trim() || null : null,
        ach_company_id: us ? form.ach_company_id.trim() || null : null,
        ledger: form.ledger || undefined,
        active: form.active,
      });
      onChanged();
      setEditing(null);
    }, t('bank.account_saved'));
  const ibanOk = !form.iban.trim() || !/^[A-Za-z]{2}\d{2}/.test(form.iban.replace(/\s+/g, '')) || ibanValid(form.iban);
  // Payment files: a BIC for SEPA; in dollars, the bank's routing number and the ACH company ID.
  const us = data.base_currency === 'USD';
  const bicOk = !form.bic.trim() || /^[A-Za-z]{6}[A-Za-z0-9]{2}([A-Za-z0-9]{3})?$/.test(form.bic.replace(/\s+/g, ''));
  const routingOk = !form.routing_no.trim() || routingValid(form.routing_no);

  if (editing) {
    return (
      <Dialog
        title={editing === 'new' ? t('bank.add_account') : t('bank.edit_account')}
        onClose={() => (data.accounts.length && !startNew ? setEditing(null) : onClose())}
        footer={
          <>
            <Button variant="ghost" onClick={() => (data.accounts.length && !startNew ? setEditing(null) : onClose())}>
              {t('common.cancel')}
            </Button>
            <Button variant="primary" icon="check" busy={busy} disabled={form.name.trim().length < 2 || !ibanOk || !bicOk || !routingOk} onClick={save}>
              {t('common.save')}
            </Button>
          </>
        }
      >
        <div className="form-grid">
          <Field label={t('bank.account_name')} required>
            <input className="input" value={form.name} maxLength={80} placeholder={t('bank.account_name_ph')} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </Field>
          <Field
            label={t('bank.iban')}
            hint={t('bank.iban_hint')}
            error={ibanOk ? null : t('bank.iban_wrong')}
            ok={form.iban.trim() && ibanOk && /^[A-Za-z]{2}\d{2}/.test(form.iban.replace(/\s+/g, '')) ? t('bank.iban_ok') : null}
          >
            <input className="input tabular" value={form.iban} maxLength={42} autoCapitalize="characters" onChange={(e) => setForm({ ...form, iban: e.target.value })} />
          </Field>
          <Field label={t('bank.bank_name')}>
            <input className="input" value={form.bank_name} maxLength={80} placeholder={t('bank.bank_name_ph')} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} />
          </Field>
          {us ? (
            <>
              <Field label={t('bank.routing_no')} hint={t('bank.routing_hint')} error={routingOk ? null : t('bank.routing_wrong')}>
                <input className="input tabular" value={form.routing_no} inputMode="numeric" maxLength={11} onChange={(e) => setForm({ ...form, routing_no: e.target.value })} />
              </Field>
              <Field label={t('bank.ach_company_id')} hint={t('bank.ach_company_hint')}>
                <input className="input tabular" value={form.ach_company_id} maxLength={10} autoCapitalize="characters" onChange={(e) => setForm({ ...form, ach_company_id: e.target.value })} />
              </Field>
            </>
          ) : (
            <Field label={t('bank.bic')} hint={t('bank.bic_hint')} error={bicOk ? null : t('bank.bic_wrong')}>
              <input className="input tabular" value={form.bic} maxLength={11} autoCapitalize="characters" onChange={(e) => setForm({ ...form, bic: e.target.value })} />
            </Field>
          )}
          <Field label={t('bank.ledger')} hint={t('bank.ledger_hint')}>
            <select className="input" value={form.ledger} onChange={(e) => setForm({ ...form, ledger: e.target.value })}>
              {data.ledgers.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.code} {l.name}
                </option>
              ))}
            </select>
          </Field>
          {editing !== 'new' ? <SwitchRow label={t('bank.in_use')} checked={form.active} onChange={(v) => setForm({ ...form, active: v })} /> : null}
          <p className="muted small">{t('bank.currency_note', { currency: data.base_currency })}</p>
        </div>
      </Dialog>
    );
  }
  return (
    <Dialog
      title={t('bank.accounts')}
      onClose={onClose}
      footer={
        <Button variant="primary" icon="plus" onClick={() => edit('new')}>
          {t('bank.add_account')}
        </Button>
      }
    >
      <ul className="bank-acc-list">
        {data.accounts.map((a) => (
          <li key={a.id}>
            <button type="button" className="bank-acc-row" onClick={() => edit(a)}>
              <Icon name="bank" size={18} />
              <span>
                <strong>{a.name}</strong>
                <span className="muted small tabular">{[a.bank_name, a.iban, a.ledger_name].filter(Boolean).join(' · ')}</span>
              </span>
              {!a.active ? <StatusBadge tone="neutral" icon="pause" label={t('bank.not_in_use')} /> : null}
              <Icon name="edit" size={16} className="muted" />
            </button>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}

/** An IBAN's check digits are right (ISO 13616 mod 97), as the server checks it. */
export function ibanValid(iban: string): boolean {
  const s = iban.replace(/\s+/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{10,30}$/.test(s)) return false;
  let rest = 0;
  for (const ch of s.slice(4) + s.slice(0, 4)) {
    const v = /\d/.test(ch) ? ch : String(ch.charCodeAt(0) - 55);
    for (const d of v) rest = (rest * 10 + Number(d)) % 97;
  }
  return rest === 1;
}

/** A US routing number: nine digits whose weighted sum (3, 7, 1) is a multiple of ten. */
export function routingValid(raw: string): boolean {
  const s = raw.replace(/[\s-]/g, '');
  if (!/^\d{9}$/.test(s)) return false;
  const w = [3, 7, 1, 3, 7, 1, 3, 7, 1];
  return [...s].reduce((sum, d, i) => sum + Number(d) * w[i]!, 0) % 10 === 0;
}
